import { useMemo, useRef, useState } from "react";
import { Check, X, ChevronDown, ChevronRight, CalendarDays, Loader2, AlertTriangle, HelpCircle, Link2, Pencil, Ban } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getCategory, PARENT_INDICATOR_COLOR } from "@/lib/categories";
import { pad2 } from "@/lib/time";
import { getAllEvents } from "@/lib/storage";
import { STATUS } from "@/lib/proposalPipeline";
import { applyItems, approvableItems } from "@/lib/proposalPackages";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

const fmt = (h, m) => (h == null || m == null ? "—" : `${pad2(h)}:${pad2(m)}`);
const DAYS_PER_PAGE = 20;

function kindLabel(it, t) {
  if (it.conflictType === "SPLIT") return t("review.kinds.split");
  return t(`review.kinds.${it.kind}`, {}) || it.kind;
}

// Dedicated Proposal Review — opens as a modal outside the chat. Groups items by
// day (collapsed, lazy-rendered), shows a summary, and supports per-item and bulk
// approve/reject. Large sets stay responsive: only expanded days render rows, and
// applying happens in batches.
export default function ProposalReview({ pkg, onClose, onDone }) {
  const { t } = useTranslation();
  const [rejected, setRejected] = useState(() => new Set());
  const [openDays, setOpenDays] = useState(() => new Set([pkg?.groupsByDate?.[0]?.date]));
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  // Synchronous guard: prevents a rapid double-click / double-approval from
  // executing the same package twice (state `busy` updates too late for that).
  const applyingRef = useRef(false);

  const groups = pkg?.groupsByDate || [];
  const pageCount = Math.max(1, Math.ceil(groups.length / DAYS_PER_PAGE));
  const pageGroups = groups.slice(page * DAYS_PER_PAGE, page * DAYS_PER_PAGE + DAYS_PER_PAGE);
  const s = pkg?.summary || {};

  const toggleDay = (d) => setOpenDays((prev) => { const n = new Set(prev); if (n.has(d)) n.delete(d); else n.add(d); return n; });
  const toggleReject = (id) => setRejected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // --- Modify (revised proposal) --------------------------------------------
  // Edits are held separately so the ORIGINAL proposal is preserved; the revised
  // values are only applied at approval time (never silently executed).
  const [edits, setEdits] = useState({});
  const [editingOpen, setEditingOpen] = useState(() => new Set());
  const toggleEditing = (id) => setEditingOpen((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const updateEdit = (id, patch) => setEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));
  const parseTime = (val, which) => {
    const [h, m] = String(val || "").split(":").map(Number);
    if (Number.isNaN(h)) return {};
    return which === "start" ? { startHour: h, startMinute: m || 0 } : { endHour: h, endMinute: m || 0 };
  };
  const applyEdit = (it) => {
    const p = edits[it.id];
    if (!p || !it.new) return it;
    return { ...it, new: { ...it.new, ...p }, revised: true, revisedFrom: it.new };
  };
  const sigOf = (it) => {
    const n = it.new || it.old || {};
    const title = String(n.title || (it.event_id && eventsById.get(it.event_id)?.title) || "").toLowerCase().trim();
    const tm = n.startHour != null ? ` ${fmt(n.startHour, n.startMinute)}` : "";
    return `${it.kind}:${title}@${it.day || n.date || ""}${tm}`.slice(0, 120);
  };

  const toApply = useMemo(
    () => approvableItems(pkg).filter((it) => !rejected.has(it.id)).map(applyEdit),
    [pkg, rejected, edits] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Look up the referenced existing event so MOVE/EDIT/DELETE rows can show the
  // event's real title (the AI's old/new payload often omits it for these ops).
  const eventsById = useMemo(() => {
    const m = new Map();
    for (const e of getAllEvents()) m.set(e.id, e);
    return m;
  }, []);

  // Resolve a parent title for the "Part of" chip: an existing event, or a
  // same-batch NEW parent create referenced by its temporary tempId.
  const parentTitleFor = (pid) => {
    if (!pid) return "—";
    const existing = eventsById.get(pid);
    if (existing) return existing.title || "—";
    for (const it of pkg?.items || []) {
      if (it.kind === "create" && it.new && it.new.tempId === pid) return it.new.title || "—";
    }
    return "—";
  };

  const doApprove = async (items) => {
    if (busy || applyingRef.current || !items.length) return;
    applyingRef.current = true;
    setBusy(true);
    const res = await applyItems(items, { onProgress: setProgress });
    setBusy(false);
    if (res.failed > 0) toast.error(t("review.appliedWithFailures", { applied: res.applied, failed: res.failed }));
    else toast.success(t("review.applied", { count: res.applied }));
    const accepted = items.map(sigOf);
    const rejectedSigs = approvableItems(pkg).filter((it) => rejected.has(it.id)).map(sigOf);
    onDone?.({ ...res, accepted, rejected: rejectedSigs });
  };

  const rejectAll = () => {
    const rejectedSigs = approvableItems(pkg).map(sigOf);
    onDone?.({ applied: 0, failed: 0, rejectedAll: true, accepted: [], rejected: rejectedSigs });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4" data-testid="proposal-review-modal">
      <div className="w-full sm:max-w-2xl bg-card rounded-t-2xl sm:rounded-2xl shadow-xl border border-border max-h-[92vh] flex flex-col">
        {/* Header + summary */}
        <div className="px-4 sm:px-5 py-3 border-b border-border flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{t("review.title")}</p>
            <p className="text-sm font-semibold text-slate-900" data-testid="review-heading">
              {t("review.summaryLine", { count: s.actionable || 0 })}
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-accent flex items-center justify-center" data-testid="review-close" aria-label={t("review.close")}>
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="px-4 sm:px-5 py-2 flex flex-wrap gap-2 text-xs border-b border-border bg-slate-50/60" data-testid="review-summary">
          {s.creates ? <Chip label={t("review.creates", { count: s.creates })} tone="emerald" /> : null}
          {s.edits ? <Chip label={t("review.edits", { count: s.edits })} tone="sky" /> : null}
          {s.deletes ? <Chip label={t("review.deletes", { count: s.deletes })} tone="rose" /> : null}
          {s.moves ? <Chip label={t("review.moves", { count: s.moves })} tone="slate" /> : null}
          {s.splits ? <Chip label={t("review.splits", { count: s.splits })} tone="amber" /> : null}
          {s.needsClarification ? <Chip label={t("review.clarify", { count: s.needsClarification })} tone="amber" icon={HelpCircle} /> : null}
          {s.blocked ? <Chip label={t("review.blockedCount", { count: s.blocked })} tone="rose" icon={Ban} /> : null}
          {s.rejected ? <Chip label={t("review.rejectedCount", { count: s.rejected })} tone="rose" icon={AlertTriangle} /> : null}
        </div>

        {/* Grouped, collapsible days (lazy) */}
        <div className="flex-1 overflow-y-auto" data-testid="review-body">
          {pageGroups.map((g) => {
            const isOpen = openDays.has(g.date);
            return (
              <div key={g.date} className="border-b border-border" data-testid={`review-day-${g.date}`}>
                <button onClick={() => toggleDay(g.date)} className="w-full px-4 sm:px-5 py-2.5 flex items-center gap-2 hover:bg-slate-50 transition" data-testid={`review-day-toggle-${g.date}`}>
                  {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                  <CalendarDays className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-800">{g.date}</span>
                  <span className="text-xs text-slate-500">· {g.items.length}</span>
                </button>
                {isOpen ? (
                  <ul className="divide-y divide-border">
                    {g.items.map((it) => {
                      const isRejected = it.status === STATUS.REJECTED;
                      const isBlocked = it.status === STATUS.BLOCKED;
                      const willReject = rejected.has(it.id) || isRejected;
                      const base = it.event_id ? eventsById.get(it.event_id) : null;
                      const ev = { ...(base || {}), ...(it.old || {}), ...(it.new || {}) };
                      const cat = getCategory(ev.category || "free_time");
                      const hasTimes = ev.startHour != null && ev.endHour != null;
                      return (
                        <li key={it.id} className="px-4 sm:px-5 py-2.5 flex items-start gap-3" data-testid={`review-item-${it.id}`}>
                          {isBlocked ? (
                            <span
                              className="mt-0.5 h-5 w-5 rounded-md border border-rose-300 bg-rose-50 flex items-center justify-center shrink-0 cursor-not-allowed"
                              data-testid={`review-blocked-check-${it.id}`}
                              aria-disabled="true"
                              title={t("review.blocked")}
                            >
                              <Ban className="h-3 w-3 text-rose-500" />
                            </span>
                          ) : (
                            <button
                              disabled={isRejected}
                              onClick={() => toggleReject(it.id)}
                              className={["mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0", willReject ? "border-slate-300 bg-white" : "bg-slate-900 border-slate-900 text-white"].join(" ")}
                              data-testid={`review-check-${it.id}`}
                            >
                              {willReject ? null : <Check className="h-3 w-3" />}
                            </button>
                          )}
                          <span className="h-6 w-6 rounded-md shrink-0 mt-0.5" style={{ backgroundColor: cat.hex }} aria-hidden />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider px-2 py-0.5">{kindLabel(it, t)}</span>
                              <span className="text-sm font-medium text-slate-900 truncate">
                                {ev.title || "—"}
                                {hasTimes ? ` · ${fmt(ev.startHour, ev.startMinute)}–${fmt(ev.endHour, ev.endMinute)}` : ""}
                              </span>
                              {it.status === STATUS.CLARIFY ? <span className="text-amber-600 text-xs inline-flex items-center gap-1"><HelpCircle className="h-3 w-3" />{t("review.needsClarify")}</span> : null}
                              {isBlocked ? <span className="text-rose-600 text-xs inline-flex items-center gap-1" data-testid={`review-blocked-badge-${it.id}`}><Ban className="h-3 w-3" />{t("review.blocked")}</span> : null}
                              {isRejected ? <span className="text-rose-600 text-xs">{t("review.invalid")}</span> : null}
                            </div>
                            {it.reason ? <p className="text-xs text-slate-500 mt-0.5">{it.reason}</p> : null}
                            {isBlocked && it.blackReasons && it.blackReasons.length ? (
                              <p className="text-xs text-rose-600 mt-0.5" data-testid={`review-blocked-reason-${it.id}`}>
                                {it.blackReasons.map((c) => t(`review.blockedReasons.${c}`, {}) || c).join(" · ")}
                              </p>
                            ) : null}
                            {it.decision && it.decision.requiresConfirmation ? (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-[10px] px-1.5 py-0.5 mt-1"
                                data-testid={`review-confirm-${it.id}`}
                                title={(it.decision.reasonCodes || []).join(", ")}
                              >
                                <AlertTriangle className="h-3 w-3" />
                                {t("review.needsConfirm")}
                              </span>
                            ) : null}
                            {(it.kind === "create" || it.kind === "edit") && !isRejected && !isBlocked ? (
                              <button
                                type="button"
                                onClick={() => toggleEditing(it.id)}
                                className="text-[11px] text-slate-500 hover:text-slate-800 mt-1 ml-2 inline-flex items-center gap-1"
                                data-testid={`review-modify-${it.id}`}
                              >
                                <Pencil className="h-3 w-3" /> {t("review.modify")}
                              </button>
                            ) : null}
                            {editingOpen.has(it.id) ? (
                              <div className="mt-2 grid grid-cols-2 gap-2 bg-slate-50 rounded-lg p-2" data-testid={`review-edit-${it.id}`}>
                                <label className="col-span-2 text-[11px] text-slate-500">
                                  {t("createEvent.eventName")}
                                  <input
                                    className="mt-0.5 w-full rounded-md border px-2 py-1 text-sm"
                                    defaultValue={ev.title || ""}
                                    onChange={(e) => updateEdit(it.id, { title: e.target.value })}
                                    data-testid={`review-edit-title-${it.id}`}
                                  />
                                </label>
                                <label className="text-[11px] text-slate-500">
                                  {t("createEvent.date")}
                                  <input
                                    type="date"
                                    className="mt-0.5 w-full rounded-md border px-2 py-1 text-sm"
                                    defaultValue={ev.date || it.day || ""}
                                    onChange={(e) => updateEdit(it.id, { date: e.target.value })}
                                    data-testid={`review-edit-date-${it.id}`}
                                  />
                                </label>
                                <div />
                                <label className="text-[11px] text-slate-500">
                                  {t("review.start")}
                                  <input
                                    type="time"
                                    className="mt-0.5 w-full rounded-md border px-2 py-1 text-sm"
                                    defaultValue={hasTimes ? fmt(ev.startHour, ev.startMinute) : ""}
                                    onChange={(e) => updateEdit(it.id, parseTime(e.target.value, "start"))}
                                    data-testid={`review-edit-start-${it.id}`}
                                  />
                                </label>
                                <label className="text-[11px] text-slate-500">
                                  {t("review.end")}
                                  <input
                                    type="time"
                                    className="mt-0.5 w-full rounded-md border px-2 py-1 text-sm"
                                    defaultValue={hasTimes ? fmt(ev.endHour, ev.endMinute) : ""}
                                    onChange={(e) => updateEdit(it.id, parseTime(e.target.value, "end"))}
                                    data-testid={`review-edit-end-${it.id}`}
                                  />
                                </label>
                                {edits[it.id] ? (
                                  <span className="col-span-2 text-[10px] text-amber-600" data-testid={`review-revised-${it.id}`}>
                                    {t("review.revised")}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            {ev.parentEventId ? (
                              <p
                                className="text-[11px] mt-0.5 inline-flex items-center gap-1 font-medium"
                                style={{ color: PARENT_INDICATOR_COLOR }}
                                data-testid={`review-partof-${it.id}`}
                              >
                                <Link2 className="h-3 w-3" />
                                {t("review.partOf", { title: parentTitleFor(ev.parentEventId) })}
                              </p>
                            ) : null}
                            {(it.conflictType && it.conflictType !== "NONE") || (it.inferred && it.inferred.length) ? (
                              <div className="flex flex-wrap gap-1 mt-1" data-testid={`review-badges-${it.id}`}>
                                {it.conflictType && it.conflictType !== "NONE" ? (
                                  <span className="inline-flex items-center rounded-full bg-violet-50 text-violet-700 text-[10px] px-1.5 py-0.5">{t("review.autoResolved")}</span>
                                ) : null}
                                {it.inferred && it.inferred.includes("category") ? (
                                  <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 text-[10px] px-1.5 py-0.5">{t("review.inferredCategory")}</span>
                                ) : null}
                                {it.inferred && it.inferred.includes("priority") ? (
                                  <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 text-[10px] px-1.5 py-0.5">{t("review.inferredPriority")}</span>
                                ) : null}
                                {it.inferred && it.inferred.includes("time") ? (
                                  <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 text-[10px] px-1.5 py-0.5">{t("review.inferredTime")}</span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            );
          })}
          {pageCount > 1 ? (
            <div className="px-4 py-2 flex items-center justify-between">
              <Button size="sm" variant="ghost" className="rounded-full" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="review-prev">‹</Button>
              <span className="text-xs text-slate-500">{t("bulk.pageOf", { page: page + 1, total: pageCount })}</span>
              <Button size="sm" variant="ghost" className="rounded-full" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)} data-testid="review-next">›</Button>
            </div>
          ) : null}
        </div>

        {/* Actions */}
        <div className="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-2 border-t border-border bg-slate-50/60">
          {progress ? <span className="text-xs text-slate-500" data-testid="review-progress">{t("review.applying", { done: progress.processed, total: progress.total })}</span> : null}
          <span className="flex-1" />
          <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={rejectAll} data-testid="review-reject-all">
            <X className="h-3.5 w-3.5 mr-1" /> {t("review.rejectAll")}
          </Button>
          <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={busy || toApply.length === 0} onClick={() => doApprove(toApply)} data-testid="review-approve-all">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
            {t("review.approveAll", { count: toApply.length })}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Chip({ label, tone = "slate", icon: Icon }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700", sky: "bg-sky-50 text-sky-700",
    rose: "bg-rose-50 text-rose-700", slate: "bg-slate-100 text-slate-700", amber: "bg-amber-50 text-amber-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${tones[tone] || tones.slate}`}>
      {Icon ? <Icon className="h-3 w-3" /> : null}{label}
    </span>
  );
}
